import { Organization } from "@packages/client";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { isJson } from "Libs/utils";
import { organizationByDescriptionIdSelector } from "Reducers/organization/organization";

import type {
  GetOrganizationProps,
  GetOrganizationPayloadFulfilled
} from "../types";
import type { AsyncThunkOptionType } from "Reducers/types";

const isLegitimateOrganization = (
  organization: Partial<Organization> | undefined
) => !!organization?._links;

const handleError = (error, shouldUpdateState: boolean, rejectWithValue) => {
  if (isJson(error)) {
    const errorAsJSON = JSON.parse(error);
    // The user needs either MFA or SSO to access the organization
    if (errorAsJSON.error === "insufficient_user_authentication") {
      // We need to patch the error since we don't have enough information
      // from the client.js
      errorAsJSON.status = 401;
      if (shouldUpdateState) {
        return rejectWithValue({
          error: errorAsJSON,
          shouldUpdateState
        });
      }
    }
  }
};

export const getOrganization = createAsyncThunk<
  GetOrganizationPayloadFulfilled,
  GetOrganizationProps,
  AsyncThunkOptionType
>(
  "app/organizations/get_organization",
  async (
    { organizationDescriptionId, shouldUpdateState = false },
    { rejectWithValue, getState }
  ) => {
    const state = getState();

    /**
     * Case 1
     * The provided "organizationDescriptionId" is the organization name and the organization is in the redux state.
     */

    let organization = organizationByDescriptionIdSelector(state, {
      organizationDescriptionId
    });

    if (isLegitimateOrganization(organization)) {
      return { organization, shouldUpdateState };
    }

    const platformLib = await import("Libs/platform");
    const client = platformLib.default;

    /**
     * Case 2
     * The provided "organizationDescriptionId" is actually the organization id and the organization name is unrelated to the organization id
     *
     * Case 3
     * The provided "organizationDescriptionId" is actually the organization id and the organization name is the lowercase version of the organization.id
     */
    try {
      organization = await client.getOrganization(organizationDescriptionId);
    } catch (error) {
      handleError(error, shouldUpdateState, rejectWithValue);
    }

    if (isLegitimateOrganization(organization)) {
      /**
       * The organization path within the url file path is displaying the organization.id
       * Console needs to displays the current organization via the organization.name
       */
      if (organization?.name) {
        const newPath = location.pathname.replace(
          organizationDescriptionId,
          organization.name
        );

        return {
          organization,
          shouldUpdateState,
          redirectTo: newPath
        };
      }
    }

    /**
     * Case 4
     * The provided "organizationDescriptionId" is the organization name, but it is not in the redux state
     *
     * For admins, they may not have the organization in their /organizations response
     */
    try {
      organization = await client.getOrganization(
        `name=${organizationDescriptionId}`
      );
    } catch (error) {
      handleError(error, shouldUpdateState, rejectWithValue);
    }

    if (isLegitimateOrganization(organization)) {
      return { organization, shouldUpdateState };
    }

    /**
     * Case 5
     * The organization doesn't exist
     */
    return rejectWithValue({
      error: new Error("organization.notfound")
    });
  }
);
